import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import clsx from 'clsx';
import { Box, Column, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ColorSelectorWithPopup } from '~/client-flow/clientOb/BrandingControllers/ColorSelectorWithPopup';
import { PrimaryBackgroundColorPopup } from '~/client-flow/clientOb/BrandingControllers/PrimaryBackgroundColorPopup';
import { useResponsive } from '~/client-flow/contexts/ResponsiveContext';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { useLocaleKeys } from '~/i18n';
import { BrandPreview } from '~/pages/BusinessDetails/BrandSettingsSection/BrandPreview/BrandPreview';
import styles from './BrandStep.module.scss';
import { useBrandStepForm } from './hooks/useBrandStepForm';
export const GetBrandStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const BrandStep = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { testId, worksAt } = param;
    var _logo_asDataURL, _logo_asDataURL1;
    const testIds = useMemo(()=>GetBrandStepTID(testId), [
        testId
    ]);
    const { BrandStepTestIds } = testIds;
    const { t } = useLocaleKeys('pages.vendorOnboarding.brandStep');
    const { isMobile } = useResponsive();
    const { branding, setBranding, logo, setLogo, save, saving } = useBrandStepForm({
        worksAt
    });
    useImperativeHandle(ref, ()=>({
            save
        }), [
        save
    ]);
    const handleLogoChange = (data)=>{
        if (data) {
            setLogo({
                file: data.file,
                asDataURL: data.asDataURL
            });
        }
    };
    const colorSelectors = /*#__PURE__*/ _jsxs(Row, {
        gap: 12,
        alignItems: "center",
        testId: BrandStepTestIds.ColorSelectors(),
        children: [
            /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                branding: branding,
                colorKey: "buttonPrimary",
                hasError: false,
                size: "l",
                useMobileSheet: isMobile,
                mobileSheetTitle: t.brandColorSheet(),
                onChange: setBranding
            }),
            /*#__PURE__*/ _jsx(PrimaryBackgroundColorPopup, {
                branding: branding,
                hasError: false,
                size: "l",
                useMobileSheet: isMobile,
                mobileSheetTitle: t.backgroundColorSheet(),
                onChange: setBranding
            }),
            /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                branding: branding,
                colorKey: "backgroundStandard",
                hasError: false,
                size: "l",
                useMobileSheet: isMobile,
                mobileSheetTitle: t.secondaryColorSheet(),
                onChange: setBranding
            })
        ]
    });
    const logoUpload = /*#__PURE__*/ _jsx(Box, {
        className: clsx(styles.logoPickerWrapper, isMobile && styles.logoPickerMobile),
        testId: BrandStepTestIds.LogoPicker(),
        children: /*#__PURE__*/ _jsx(LogoPicker, {
            isNewUI: true,
            image: (_logo_asDataURL = logo.asDataURL) !== null && _logo_asDataURL !== void 0 ? _logo_asDataURL : '',
            onChange: handleLogoChange,
            testId: "".concat(testId, "-logo"),
            showEditButton: true,
            editButtonLabel: t.changeLogo()
        })
    });
    const preview = /*#__PURE__*/ _jsx(BrandPreview, {
        testId: BrandStepTestIds.Preview(),
        className: clsx(!isMobile && styles.preview),
        image: (_logo_asDataURL1 = logo.asDataURL) !== null && _logo_asDataURL1 !== void 0 ? _logo_asDataURL1 : '',
        brandColor: branding.buttonPrimary,
        primaryBgStart: branding.backgroundGradientStart,
        primaryBgEnd: branding.backgroundGradientEnd,
        secondaryBgColor: branding.backgroundStandard,
        loading: saving
    });
    if (isMobile) {
        return /*#__PURE__*/ _jsxs(Column, {
            className: styles.mobileRoot,
            gap: 16,
            testId: BrandStepTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h5",
                            weight: "normal",
                            testId: BrandStepTestIds.Title(),
                            children: t.title()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "secondary",
                            testId: BrandStepTestIds.Subtitle(),
                            children: t.subtitle()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.mobilePreview,
                    children: preview
                }),
                logoUpload,
                /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    justifyContent: "space-between",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "secondary",
                            testId: BrandStepTestIds.BrandColorsLabel(),
                            children: t.brandColors()
                        }),
                        colorSelectors
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        className: styles.root,
        testId: BrandStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                className: styles.leftColumn,
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h5",
                                weight: "normal",
                                testId: BrandStepTestIds.Title(),
                                children: t.title()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                skin: "secondary",
                                testId: BrandStepTestIds.Subtitle(),
                                children: t.subtitle()
                            })
                        ]
                    }),
                    logoUpload,
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 12,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                skin: "secondary",
                                testId: BrandStepTestIds.BrandColorsLabel(),
                                children: t.brandColors()
                            }),
                            colorSelectors
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Box, {
                className: styles.previewWrapper,
                children: preview
            })
        ]
    });
});
BrandStep.displayName = 'BrandStep';
